import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MIcon from './MIcon';

const PRIO_COLOR = { critical:'#B91C1C', high:'#0085CA', medium:'#B45309', low:'#15803D' };
const EVENT_LABELS = {
  ticket_new:       { icon:'🎫', label:'Nuevo ticket',  color:'#0085CA' },
  ticket_updated:   { icon:'✏️', label:'Ticket actualizado', color:'#7C3AED' },
  ticket_escalated: { icon:'⬆️', label:'Ticket escalado', color:'#B45309' },
  comment_added:    { icon:'💬', label:'Nuevo comentario', color:'#0085CA' },
  comment_internal: { icon:'🔒', label:'Nota interna', color:'#B45309' },
};

export default function NotificationCenter({ onlineAgents = [] }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread,        setUnread]        = useState(0);
  const [open,          setOpen]          = useState(false);
  const ref = useRef(null);

  // Cerrar al clic fuera
  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  function addNotification(event) {
    const key = event.type === 'comment_added'
      ? (event.comment?.is_internal ? 'comment_internal' : 'comment_added')
      : event.type;
    const meta = EVENT_LABELS[key];
    if (!meta) return;

    const n = {
      id:    Date.now(),
      type:  event.type,
      icon:  meta.icon,
      label: meta.label,
      color: meta.color,
      ticket: event.ticket || (event.ticketId ? { id: event.ticketId } : null),
      extra: event.comment ? { author: event.comment.author_name, body: event.comment.body } : null,
      time:  new Date(),
      read:  false,
    };

    setNotifications(prev => [n, ...prev].slice(0, 50));
    setUnread(prev => prev + 1);

    // Toast en esquina superior derecha
    showToast(n);
  }

  function escapeHtml(s='') {
    return s.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  function showToast(n) {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position:fixed; bottom:24px; right:24px; z-index:9999;
      background:#fff; border:1px solid #E5E7EB; border-left:4px solid ${n.color};
      border-radius:10px; padding:12px 16px; min-width:280px; max-width:360px;
      box-shadow:0 8px 24px rgba(0,0,0,.12); cursor:pointer;
      animation: slideIn .3s ease; font-family: 'Instrument Sans', sans-serif;
    `;

    const style = document.createElement('style');
    style.textContent = `@keyframes slideIn { from { transform:translateX(110%); opacity:0; } to { transform:translateX(0); opacity:1; } }`;
    document.head.appendChild(style);

    toast.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
        <span style="font-size:16px">${n.icon}</span>
        <span style="font-size:12px;font-weight:700;color:${n.color}">${n.label}</span>
      </div>
      <div style="font-size:13px;color:#111827;font-weight:500">${n.ticket?.title || ''}</div>
      ${n.extra ? `<div style="font-size:12px;color:#374151;margin-top:3px"><strong>${escapeHtml(n.extra.author||'')}:</strong> ${escapeHtml((n.extra.body||'').slice(0,90))}${(n.extra.body||'').length>90?'…':''}</div>` : ''}
      ${n.ticket?.ref ? `<div style="font-size:11px;color:#9CA3AF;margin-top:2px">${n.ticket.ref} · ${n.ticket?.priority ? `Prioridad ${n.ticket.priority}` : ''}</div>` : ''}
    `;

    toast.onclick = () => {
      if (n.ticket?.id) navigate(`/backdesk/tickets/${n.ticket.id}`);
      toast.remove();
    };

    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'slideIn .3s ease reverse';
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  // Exponer addNotification al padre via ref
  NotificationCenter.addNotification = addNotification;

  function markAllRead() {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  }

  return (
    <div ref={ref} style={{position:'relative'}}>
      {/* Botón campana */}
      <button
        onClick={() => { setOpen(o => !o); if (!open) markAllRead(); }}
        style={{
          position:'relative', width:36, height:36, borderRadius:8,
          background: open ? '#F3F4F6' : 'transparent',
          border:'1px solid #E5E7EB', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:16,
        }}>
        <MIcon name="notifications" size={19} color={open || unread > 0 ? 'var(--bd-accent, #0085CA)' : '#6B7280'} />
        {unread > 0 && (
          <span style={{
            position:'absolute', top:-4, right:-4,
            width:16, height:16, borderRadius:'50%',
            background:'#B91C1C', color:'#fff',
            fontSize:9, fontWeight:700,
            display:'flex', alignItems:'center', justifyContent:'center',
            border:'2px solid #fff',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {/* Panel de notificaciones */}
      {open && (
        <div style={{
          position:'absolute', top:44, right:0,
          width:360, maxHeight:480,
          background:'#fff', border:'1px solid #E5E7EB',
          borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,.12)',
          overflow:'hidden', zIndex:1000,
          display:'flex', flexDirection:'column',
        }}>
          <div style={{padding:'12px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#FAFAFA'}}>
            <span style={{fontSize:13, fontWeight:700, color:'#111827'}}>Notificaciones</span>
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              {/* Agentes online */}
              <span style={{fontSize:11, color:'#15803D', fontWeight:600, background:'#F0FDF4', padding:'2px 8px', borderRadius:10, border:'1px solid #86EFAC'}}>
                ● {onlineAgents.length} online
              </span>
              {notifications.length > 0 && (
                <button onClick={markAllRead} style={{fontSize:11, color:'#6B7280', background:'none', border:'none', cursor:'pointer'}}>
                  Marcar todas leídas
                </button>
              )}
            </div>
          </div>

          <div style={{overflowY:'auto', flex:1}}>
            {notifications.length === 0 ? (
              <div style={{padding:32, textAlign:'center', color:'#9CA3AF', fontSize:13}}>
                <div style={{fontSize:32, marginBottom:8}}>🔔</div>
                Sin notificaciones
              </div>
            ) : (
              notifications.map(n => (
                <div key={n.id}
                  onClick={() => { if (n.ticket?.id) { navigate(`/backdesk/tickets/${n.ticket.id}`); setOpen(false); }}}
                  style={{
                    padding:'12px 16px', borderBottom:'1px solid #F9FAFB',
                    cursor: n.ticket?.id ? 'pointer' : 'default',
                    background: n.read ? '#fff' : '#F8FAFF',
                    borderLeft: `3px solid ${n.read ? 'transparent' : n.color}`,
                  }}>
                  <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:3}}>
                    <span style={{fontSize:14}}>{n.icon}</span>
                    <span style={{fontSize:11, fontWeight:700, color:n.color}}>{n.label}</span>
                    <span style={{fontSize:10, color:'#9CA3AF', marginLeft:'auto'}}>
                      {n.time.toLocaleTimeString('es-ES', {hour:'2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                  {n.ticket && (
                    <div style={{paddingLeft:22}}>
                      <div style={{fontSize:12, color:'#111827', fontWeight:500}}>{n.ticket.title || n.ticket.ref}</div>
                      <div style={{fontSize:11, color:'#9CA3AF'}}>{n.ticket.ref}</div>
                      {n.extra && (
                        <div style={{fontSize:11, color:'#6B7280', marginTop:3}}>
                          <strong>{n.extra.author}:</strong> {(n.extra.body||'').slice(0,80)}{(n.extra.body||'').length>80?'…':''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
